import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ButtonGroup, Text } from '@paygreen/paygreen-ui';
import UploadLogoForm from './UploadLogoForm';
import {
    ModalForm,
    CancelButton,
    ValidateButton,
    Loader,
} from '../../../components';
import { putAssociation } from '../service';

const UploadLogoModal = ({
    setOpen,
    isOpen,
    t,
    charityData,
    setCharityListData,
    idAssociation,
    updateData,
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [tempFile, setTempFile] = useState();
    const [base64, setBase64] = useState();
    const [isLoading, setIsLoading] = useState(false);

    // initializing all react-hook-form values and methods
    const defaultValues = {
        inputImage: '',
    };

    const { register, handleSubmit, reset, errors, setError, clearErrors } =
        useForm();

    const cancelCloseModal = useCallback(() => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            setErrorMessage();
            setTempFile();
            setBase64();
            reset();
        }, 500);
    }, [setOpen, reset]);

    /**
     * Put request to API to add / modify chraity's logo
     * @param {string} id - association id
     * @param {object} charity - object with new logo and others texts for charity description
     */
    const addLogo = useCallback(
        (id, charity) => {
            setErrorMessage();
            setIsLoading(true);

            putAssociation(id, charity)
                .then((res) => {
                    cancelCloseModal();
                    setCharityListData && setCharityListData([res.data]);
                    updateData && updateData(res.data);

                    !isLoading &&
                        toast(
                            <Text colorPallet="theme" textSize="xs">
                                <Trans i18nKey="form.toastSuccess.addLogo">
                                    Logo added
                                    <Text
                                        htmlTag="strong"
                                        colorPallet="theme"
                                        textSize="xs"
                                    >
                                        {{
                                            logoName: tempFile.name,
                                        }}
                                    </Text>
                                    successfully
                                </Trans>
                            </Text>,
                        );
                })
                .catch((error) => setErrorMessage(error?.response?.data.detail))
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [cancelCloseModal, isLoading, setCharityListData, tempFile, updateData],
    );

    const submitForm = () => {
        setErrorMessage(null);

        const { name, description, url, fieldOfAction, visible } = charityData;

        let updatedData = {
            name,
            description,
            url,
            fieldOfAction,
            picture1: base64,
            visible,
        };

        addLogo(idAssociation, updatedData);
    };

    return (
        <ModalForm
            modalTitle={t('charity.see.modalTitle')}
            blockWidth="xl"
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            <UploadLogoForm
                errors={errors}
                clearErrors={clearErrors}
                register={register}
                reset={reset}
                onSubmit={handleSubmit(submitForm)}
                defaultValues={defaultValues}
                tempFile={tempFile}
                setTempFile={setTempFile}
                setErrorMessage={setErrorMessage}
                errorMessage={errorMessage}
                setError={setError}
                setBase64={setBase64}
                base64={base64}
            >
                {tempFile && (
                    <ButtonGroup marginTop="xs" marginBottom="sm">
                        <CancelButton
                            buttonSize="md"
                            onClick={cancelCloseModal}
                        >
                            {t('form.button.cancel')}
                        </CancelButton>

                        <ValidateButton
                            buttonSize="md"
                            isDisabled={isLoading}
                            type="submit"
                        >
                            {t('resetPassword.validateButton')}
                        </ValidateButton>
                    </ButtonGroup>
                )}
            </UploadLogoForm>

            {isLoading && <Loader isLoading={isLoading} />}
        </ModalForm>
    );
};

UploadLogoModal.propTypes = {
    charityData: PropTypes.object,
    setCharityListData: PropTypes.func,
    idAssociation: PropTypes.string,
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
};

UploadLogoModal.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(UploadLogoModal);
